import {
  API_BASE,
  IDENTITY_SERVICE_BASE_URL,
  TASK_SERVICE_BASE_URL,
  normalizePage,
  Page,
  serviceFetch,
  serviceJson,
} from './mobileParityApi';

export type RootCategory = {
  id: string;
  name: string;
  description?: string;
  type?: 'ROOT' | string;
};

export type CategoryPreference = {
  id?: string;
  helperId: string;
  categoryId: string;
  enabled: boolean;
  updatedAt?: string;
};

export type SecurityQuestionDTO = {
  id: string;
  questionText: string;
  active: boolean;
};

export type SecurityQuestionsPayload = {
  question1Id: string;
  answer1: string;
  question2Id: string;
  answer2: string;
  question3Id: string;
  answer3: string;
  password: string;
};

export type SupportedLocation = {
  id?: string;
  country?: string;
  state?: string;
  county?: string;
  city?: string;
  accessType?: string;
  enabled?: boolean;
  active?: boolean;
};

export async function fetchRootTaskCategories(): Promise<RootCategory[]> {
  const raw = await serviceJson<RootCategory[]>(
    `${TASK_SERVICE_BASE_URL}/api/v1/task-categories/roots`,
    { method: 'GET', acceptJson: true },
    'Failed to load notification categories'
  );
  return Array.isArray(raw) ? raw : [];
}

export async function fetchHelperCategoryPreference(
  helperId: string,
  categoryId: string
): Promise<CategoryPreference | null> {
  return serviceJson<CategoryPreference | null>(
    `${TASK_SERVICE_BASE_URL}/api/v1/helper-category-notifications/helper/${encodeURIComponent(helperId)}/category/${encodeURIComponent(categoryId)}`,
    { method: 'GET', acceptJson: true },
    'Failed to load category notification preference'
  ).catch(() => null);
}

export async function updateHelperCategoryPreference(
  helperId: string,
  categoryId: string,
  enabled: boolean
): Promise<CategoryPreference> {
  return serviceJson<CategoryPreference>(
    `${TASK_SERVICE_BASE_URL}/api/v1/helper-category-notifications/helper/${encodeURIComponent(helperId)}/category/${encodeURIComponent(categoryId)}`,
    {
      method: 'PUT',
      body: JSON.stringify({ enabled }),
    },
    'Failed to update category notification preference'
  );
}

export async function changePassword(
  currentPassword: string,
  newPassword: string
): Promise<void> {
  await serviceFetch(
    `${IDENTITY_SERVICE_BASE_URL}/api/v1/auth/password`,
    {
      method: 'PUT',
      body: JSON.stringify({
        newPassword,
        ...(currentPassword ? { currentPassword } : {}),
      }),
    },
    'Failed to update password'
  );
}

export async function fetchSecurityQuestions(): Promise<SecurityQuestionDTO[]> {
  const raw = await serviceJson<SecurityQuestionDTO[] | Page<SecurityQuestionDTO>>(
    `${IDENTITY_SERVICE_BASE_URL}/api/v1/security-questions?active=true&page=0&size=200`,
    { method: 'GET', acceptJson: true },
    'Failed to load security questions'
  );
  return normalizePage(raw).content.filter((question) => question.active !== false);
}

export async function updateSecurityQuestions(payload: SecurityQuestionsPayload): Promise<void> {
  await serviceFetch(
    `${IDENTITY_SERVICE_BASE_URL}/api/v1/auth/security-questions`,
    {
      method: 'PUT',
      body: JSON.stringify(payload),
    },
    'Failed to save security questions'
  );
}

export async function createUserDeletionRequest(payload: {
  deletionReason?: string | null;
}): Promise<void> {
  await serviceFetch(
    `${IDENTITY_SERVICE_BASE_URL}/api/v1/user-deletion-requests`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
    'Failed to submit account deletion request'
  );
}

export async function createUserSuspensionRequest(payload: {
  suspensionReason?: string | null;
}): Promise<void> {
  await serviceFetch(
    `${IDENTITY_SERVICE_BASE_URL}/api/v1/user-suspension-requests`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
    'Failed to submit account suspension request'
  );
}

export async function fetchSupportedLocations(): Promise<SupportedLocation[]> {
  let page = 0;
  const size = 100;
  let totalPages = 1;
  const collected: SupportedLocation[] = [];

  while (page < totalPages) {
    const params = new URLSearchParams({
      page: String(page),
      size: String(size),
      sort: 'createdAt,desc',
    });

    const raw = await serviceJson<SupportedLocation[] | Page<SupportedLocation>>(
      `${API_BASE}/api/v1/location-restrictions?${params}`,
      { method: 'GET', acceptJson: true },
      'Failed to load supported locations'
    );
    const normalized = normalizePage(raw);
    collected.push(
      ...normalized.content
        .map((item) => ({
          id: item.id ? String(item.id) : undefined,
          country: item.country ? String(item.country) : '',
          state: item.state ? String(item.state) : '',
          county: item.county ? String(item.county) : '',
          city: item.city ? String(item.city) : '',
          accessType: item.accessType ? String(item.accessType) : '',
          enabled: item.enabled !== false,
          active: item.active !== false,
        }))
        .filter((item) => {
          const accessType = String(item.accessType || '').toUpperCase();
          return (item.active ?? true) && (item.enabled ?? true) && accessType === 'ALLOWED';
        })
    );

    totalPages = normalized.totalPages && normalized.totalPages > 0 ? normalized.totalPages : 1;
    page += 1;
  }

  return collected;
}
